import React, { useEffect, useId, useRef, useState } from 'react';
import {
  CloseOutlined,
  LockOutlined,
  PlusOutlined,
  PushpinOutlined,
  RightOutlined,
  SearchOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import { UseOrbitSessionsResult } from 'api/orbit';
import { visuallyHidden } from 'components/Intelligence/visuallyHidden';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { ORBIT_WORKSPACES } from '../../orbitWorkspaces';
import { OrbitSession, OrbitWorkspaceTag } from '../../types';
import { ORBIT_PAGE_MOCK } from '../orbitPageMock';
import useOrbitRailLoadMore, { IOrbitRailLoadMore } from '../useOrbitRailLoadMore';
import { OrbitUnsubscribedState } from '../useOrbitUnsubscribedFeatures';
import OrbitChatRowIcon from './OrbitChatRowIcon';
import OrbitWorkspaceFlyout from './OrbitWorkspaceFlyout';
import { noop, pg, PlainButton, tint, type } from './pageStyles';

/** Figma 6473:3004 / 6473:2941: a hovered or selected row is a full-width, square-cornered band. */
const RAIL_BAND = '#15203E';

const Rail = styled.aside`
  display: flex;
  flex: 0 0 292px;
  flex-direction: column;
  width: 292px;
  height: 100%;
  min-height: 0;
  border-right: 1px solid ${pg.border};
  background: ${pg.rail};
`;

const Header = styled.div`
  flex: none;
  padding: 18px 18px 14.8px;
  border-bottom: 1px solid ${pg.border};
`;

/** The rail's lists scroll under its fixed header. */
const RailScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 23px;
  overflow-y: auto;
  ${intelligenceScrollbar('y')}
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const Title = styled.h2`
  margin: 0;
  color: ${pg.text};
  ${type('pageTitle', 700)}
`;

const Subtitle = styled.p`
  margin: 2px 0 0;
  color: ${pg.muted};
  ${type('containerBody')}
`;

/** Search at x 221 and split view at x 252, both centred on y 35. */
const HeaderIcons = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  height: 16px;
  margin: 9px 5px 0 0;

  > * + * {
    margin-left: 17px;
  }
`;

const HeaderIcon = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
`;

// No frame draws the open search; it takes the composer's field look at the rail's scale.
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  height: 34px;
  margin-top: 14px;
  padding: 0 10px;
  border: 0.8px solid ${pg.border};
  border-radius: 10px;
  background: ${pg.card};
  color: ${pg.muted};

  &:focus-within {
    border-color: ${pg.link};
  }

  > * + * {
    margin-left: 8px;
  }
`;

const SearchField = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${pg.text};
  ${type('containerBody')}

  &::placeholder {
    color: ${pg.placeholder};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`;

const NewChat = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 39px;
  margin-top: 16px;
  border-radius: 10px;
  background: linear-gradient(90deg, ${pg.violet}, ${pg.link});
  color: #ffffff;
  ${type('action', 700)}

  > * + * {
    margin-left: 8px;
  }
`;

// Rows span the rail so their hover band can; headings and links keep the 14px inset.
const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding-top: 21px;

  &:first-child {
    padding-top: 14px;
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  margin: 0 14px 5px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${pg.text};
  ${type('containerBody', 800)}
`;

const RailLink = styled(PlainButton)`
  align-self: center;
  margin-top: 7px;
  color: ${pg.link};
  ${type('helping', 800)}
  line-height: 13px;
`;

const ChatRow = styled(PlainButton)`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 29px;
  margin: 1px 0;
  padding: 0 21px;
  color: ${pg.secondary};
  ${type('containerBody')}

  &:hover {
    background: ${RAIL_BAND};
  }

  > :first-child {
    flex: none;
    margin-right: 9px;
    color: ${pg.muted};
  }

  > :nth-child(2) {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > :nth-child(3) {
    flex: none;
    margin-left: 9px;
    color: ${pg.muted};
  }
`;

const NavRow = styled(PlainButton)`
  display: flex;
  align-items: center;
`;

// The band fills the row's own box at full rail width, so it grows with the content: 6473:2943's
// 10/8 padding (8 inside the rail's 14 inset) around it. Drawn as a 38 band centred in the row
// (6473:2941); the user sized it to the content instead (2026-10-30). The row and tile draw no fill
// of their own in any state. 6685:3301 / 6473:2941: the open chat's Workspace, and the row whose
// flyout is open, keep it.
const WorkspaceRow = styled(NavRow)<{ $selected: boolean }>`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: 10px 22px;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    content: '';
  }

  &:hover::before,
  &:focus-visible::before,
  &:active::before {
    background: ${RAIL_BAND};
  }

  ${({ $selected }) => ($selected ? `&::before { background: ${RAIL_BAND}; }` : '')}
`;

const LockedRow = styled(NavRow)`
  min-height: 49px;
  margin: 0 14px;
  padding: 9px 8px;
  border-radius: 9px;

  &:hover {
    background: ${pg.card};
  }
`;

const IconTile = styled.span<{ $color: string; $size: number; $glyph: number }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  margin-right: 9px;
  border-radius: 8px;
  background: ${({ $color }) => tint($color, 0.14)};
  color: ${({ $color }) => $color};
  font-size: ${({ $glyph }) => $glyph}px;
`;

const NavText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const NavTitle = styled.span<{ $muted?: boolean }>`
  display: block;
  overflow: hidden;
  color: ${({ $muted }) => ($muted ? pg.secondary : pg.text)};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${type('containerBody', 700)}
`;

const NavSubtitle = styled.span`
  display: block;
  overflow: hidden;
  color: ${pg.muted};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${type('helping')}
`;

/** The drawn rail lists four chats a section; each See More adds as many again. */
const RAIL_BATCH = 4;

/** Figma 6473:2944, as a literal so its 14% tile never depends on how the palette is declared. */
const WORKSPACE_TILE = '#268AFF';

/** A chat row: the chat and its "…" side by side under one band. */
const RecentRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  margin: 1px 0;
  padding-right: 17px;
  background: ${({ $active }) => ($active ? RAIL_BAND : 'transparent')};

  &:hover {
    background: ${RAIL_BAND};
  }
`;

const RecentChat = styled(ChatRow)<{ $active: boolean }>`
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  margin: 0;
  padding-right: 9px;
  color: ${({ $active }) => ($active ? pg.text : pg.secondary)};

  ${({ $active }) => ($active ? `&& > :first-child { color: ${pg.text}; }` : '')}
`;

// A demo chat pinned above the pinned chats; banded while it is the open chat, as a chat row is.
const PinnedDemoRow = styled(ChatRow)<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? RAIL_BAND : 'transparent')};
  color: ${({ $active }) => ($active ? pg.text : pg.secondary)};

  ${({ $active }) => ($active ? `&& > :first-child { color: ${pg.text}; }` : '')}
`;

// 6473:3004 draws the "…" on the banded row only: shown while the row is hovered, current, focused
// or has its menu open.
const RowMenuSlot = styled.span<{ $shown: boolean }>`
  display: inline-flex;
  flex: none;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};

  ${RecentRow}:hover &,
  &:focus-within,
  &:has([aria-expanded='true']) {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }
`;

const RailNote = styled.p`
  margin: 4px 14px;
  color: ${pg.muted};
  ${type('helping')}
`;

const Announcement = styled.span`
  ${visuallyHidden}
`;

const HiddenNote = styled.span`
  ${visuallyHidden}
`;

/**
 * Figma 6473:3775, the split-view glyph: an 11.33 x 10 frame at (2.33, 3) and a divider at x 6.33,
 * both stroked 1.17. The frame's corner radius is not in the dump; 2 is read off the render.
 */
const RailPanelIcon: React.FC = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" focusable="false" aria-hidden="true">
    <rect
      x="2.334"
      y="3"
      width="11.333"
      height="10"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.1667"
    />
    <path d="M6.334 3V13" stroke="currentColor" strokeWidth="1.1667" />
  </svg>
);

interface IRailSeeMoreProps {
  more: IOrbitRailLoadMore;
  testId: string;
}

const RailSeeMore: React.FC<IRailSeeMoreProps> = ({ more, testId }) => {
  if (!more.canShowMore) {
    return null;
  }

  let label = 'See More';
  if (more.loading) {
    label = 'Loading…';
  } else if (more.failed) {
    label = 'Retry';
  }

  // aria-disabled rather than disabled, so the focused control keeps focus while a page loads.
  return (
    <RailLink
      type="button"
      data-testid={testId}
      aria-disabled={more.loading ? true : undefined}
      aria-busy={more.loading ? true : undefined}
      onClick={more.showMore}
    >
      {label}
    </RailLink>
  );
};

interface IRailAnnouncementProps {
  more: IOrbitRailLoadMore;
  noun: string;
  testId: string;
}

const RailAnnouncement: React.FC<IRailAnnouncementProps> = ({ more, noun, testId }) => (
  <Announcement aria-live="polite" data-testid={testId}>
    {more.announcement === null
      ? ''
      : `Showing ${more.announcement.shown} ${noun}, ${more.announcement.added} added`}
  </Announcement>
);

interface IRailChatListProps {
  list: UseOrbitSessionsResult;
  idPrefix: string;
  rowTestId: string;
  moreTestId: string;
  announcementTestId: string;
  noun: string;
  /** Drawn once the list has loaded with no chat in it. */
  empty: React.ReactNode;
  /** Pinned rows end in the pin glyph. */
  pinnedGlyph?: boolean;
  activeSessionId: string | null;
  onSelectChat: (session: OrbitSession) => void;
  renderRowMenu: (session: OrbitSession, titleId: string) => React.ReactNode;
}

const RailChatList: React.FC<IRailChatListProps> = ({
  list,
  idPrefix,
  rowTestId,
  moreTestId,
  announcementTestId,
  noun,
  empty,
  pinnedGlyph = false,
  activeSessionId,
  onSelectChat,
  renderRowMenu
}) => {
  const more = useOrbitRailLoadMore({
    total: list.sessions.length,
    batch: RAIL_BATCH,
    hasMore: list.hasMore,
    loadingMore: list.isLoadingMore,
    failed: Boolean(list.error),
    onLoadMore: list.loadMore,
    onRetry: list.reload
  });

  return (
    <>
      {list.sessions.slice(0, more.shown).map((session, index) => {
        const active = session.session_id === activeSessionId;
        const titleId = `${idPrefix}-${index}`;
        return (
          <RecentRow key={session.session_id} $active={active} data-testid={rowTestId}>
            <RecentChat
              ref={index === more.firstNewIndex ? more.firstNewRowRef : undefined}
              type="button"
              $active={active}
              aria-current={active ? 'true' : undefined}
              onClick={() => onSelectChat(session)}
            >
              <OrbitChatRowIcon />
              <span id={titleId}>{session.title}</span>
              {pinnedGlyph ? <PushpinOutlined aria-hidden="true" /> : null}
            </RecentChat>
            <RowMenuSlot $shown={active}>{renderRowMenu(session, titleId)}</RowMenuSlot>
          </RecentRow>
        );
      })}
      {list.hasLoaded && list.sessions.length === 0 ? empty : null}
      {more.failed ? <RailNote role="alert">Couldn’t load more chats.</RailNote> : null}
      <RailSeeMore more={more} testId={moreTestId} />
      <RailAnnouncement more={more} noun={noun} testId={announcementTestId} />
    </>
  );
};

export interface IOrbitRailSearch {
  query: string;
  onQueryChange: (query: string) => void;
  /** The chats matching the settled query; null while there is none. */
  results: UseOrbitSessionsResult | null;
}

interface IOrbitPageRailProps {
  onNewChat: () => void;
  unsubscribed: OrbitUnsubscribedState;
  onOpenUnsubscribed: (featureId?: string) => void;
  /** The chats in no Workspace. */
  recent: UseOrbitSessionsResult;
  pinned: UseOrbitSessionsResult;
  search: IOrbitRailSearch;
  activeSessionId: string | null;
  onSelectChat: (session: OrbitSession) => void;
  /** The Workspace whose chats the flyout shows. */
  openWorkspace: OrbitWorkspaceTag | null;
  /** That Workspace's chats; null while no flyout is open. */
  workspaceChats: UseOrbitSessionsResult | null;
  onCloseWorkspace: () => void;
  /** The open chat's Workspace: its row keeps the band. */
  chatWorkspace: OrbitWorkspaceTag | null;
  onOpenWorkspace: (tag: OrbitWorkspaceTag) => void;
  /** A chat row's "…" menu; `titleId` names the element holding the row's title. */
  renderRowMenu: (session: OrbitSession, titleId: string) => React.ReactNode;
  /** DEMO ONLY: seeded chats listed at the top of Pinned. */
  pinnedDemos?: IOrbitPageRailDemo[];
  activeDemoId?: string | null;
  onSelectDemo?: (id: string) => void;
}

export interface IOrbitPageRailDemo {
  id: string;
  title: string;
}

const OrbitPageRail: React.FC<IOrbitPageRailProps> = ({
  onNewChat,
  unsubscribed,
  onOpenUnsubscribed,
  recent,
  pinned,
  search,
  activeSessionId,
  onSelectChat,
  openWorkspace,
  workspaceChats,
  onCloseWorkspace,
  chatWorkspace,
  onOpenWorkspace,
  renderRowMenu,
  pinnedDemos = [],
  activeDemoId = null,
  onSelectDemo = noop
}) => {
  const { rail } = ORBIT_PAGE_MOCK;
  const { t } = useMultiLanguageTranslation();
  const rowIdPrefix = useId();
  const flyoutId = useId();
  const workspaceRows = useRef<Partial<Record<OrbitWorkspaceTag, HTMLButtonElement | null>>>({});
  const flyoutAnchor = openWorkspace === null ? null : workspaceRows.current[openWorkspace];
  const flyoutWorkspace = ORBIT_WORKSPACES.find(({ tag }) => tag === openWorkspace);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const lists = { activeSessionId, onSelectChat, renderRowMenu };

  useEffect(() => {
    if (searchOpen) {
      searchFieldRef.current?.focus();
    }
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    search.onQueryChange('');
    searchToggleRef.current?.focus();
  };

  return (
    <Rail data-testid="orbit-page-rail" aria-label="Orbit workspace">
      <Header>
        <HeaderTop>
          <div>
            <Title>{rail.title}</Title>
            <Subtitle>{rail.subtitle}</Subtitle>
          </div>
          <HeaderIcons>
            <HeaderIcon
              ref={searchToggleRef}
              type="button"
              aria-label={t(`${TOrbit}.page.search.open`)}
              aria-expanded={searchOpen}
              onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
            >
              <SearchOutlined aria-hidden="true" />
            </HeaderIcon>
            <HeaderIcon type="button" aria-label="Collapse sidebar" onClick={noop}>
              <RailPanelIcon />
            </HeaderIcon>
          </HeaderIcons>
        </HeaderTop>
        {searchOpen ? (
          <SearchBox>
            <SearchOutlined aria-hidden="true" />
            <SearchField
              ref={searchFieldRef}
              type="search"
              aria-label={t(`${TOrbit}.page.search.open`)}
              placeholder={t(`${TOrbit}.page.search.placeholder`)}
              value={search.query}
              onChange={event => search.onQueryChange(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeSearch();
                }
              }}
            />
            <HeaderIcon
              type="button"
              aria-label={t(`${TOrbit}.page.search.close`)}
              onClick={closeSearch}
            >
              <CloseOutlined aria-hidden="true" />
            </HeaderIcon>
          </SearchBox>
        ) : null}
        <NewChat type="button" onClick={onNewChat}>
          <PlusOutlined aria-hidden="true" />
          <span>New chat</span>
        </NewChat>
      </Header>
      <RailScroll data-testid="orbit-page-rail-scroll">
        {search.results !== null ? (
          <Section aria-label={t(`${TOrbit}.page.search.results`)}>
            <SectionHead>
              <SectionTitle>{t(`${TOrbit}.page.search.results`)}</SectionTitle>
            </SectionHead>
            <RailChatList
              {...lists}
              list={search.results}
              idPrefix={`${rowIdPrefix}-search`}
              rowTestId="orbit-page-rail-search-row"
              moreTestId="orbit-page-rail-search-more"
              announcementTestId="orbit-page-rail-search-announcement"
              noun="matching chats"
              empty={<RailNote>{t(`${TOrbit}.page.search.empty`)}</RailNote>}
            />
          </Section>
        ) : (
          <>
            <Section aria-label="Pinned">
              <SectionHead>
                <SectionTitle>Pinned</SectionTitle>
              </SectionHead>
              {pinnedDemos.map(demo => {
                const active = demo.id === activeDemoId;
                return (
                  <PinnedDemoRow
                    key={demo.id}
                    type="button"
                    $active={active}
                    aria-current={active ? 'true' : undefined}
                    data-testid="orbit-page-rail-pinned-demo"
                    onClick={() => onSelectDemo(demo.id)}
                  >
                    <OrbitChatRowIcon />
                    <span>{demo.title}</span>
                    <PushpinOutlined aria-hidden="true" />
                  </PinnedDemoRow>
                );
              })}
              <RailChatList
                {...lists}
                list={pinned}
                idPrefix={`${rowIdPrefix}-pinned`}
                rowTestId="orbit-page-rail-pinned-row"
                moreTestId="orbit-page-rail-pinned-more"
                announcementTestId="orbit-page-rail-pinned-announcement"
                noun="pinned chats"
                empty={<RailNote>{t(`${TOrbit}.page.pinnedEmpty`)}</RailNote>}
                pinnedGlyph
              />
            </Section>

            <Section aria-label="Recent chats">
              <SectionHead>
                <SectionTitle>Recent chats</SectionTitle>
              </SectionHead>
              <RailChatList
                {...lists}
                list={recent}
                idPrefix={`${rowIdPrefix}-recent`}
                rowTestId="orbit-page-rail-recent-row"
                moreTestId="orbit-page-rail-recent-more"
                announcementTestId="orbit-page-rail-recent-announcement"
                noun="recent chats"
                empty={<RailNote>No conversations yet</RailNote>}
              />
            </Section>

            <Section aria-label="Workspaces">
              <SectionHead>
                <SectionTitle>Workspaces</SectionTitle>
              </SectionHead>
              {ORBIT_WORKSPACES.map(({ tag, title, subtitle, Icon }) => {
                const isOpen = tag === openWorkspace;
                const isChats = tag === chatWorkspace;
                return (
                  <WorkspaceRow
                    key={tag}
                    ref={row => {
                      workspaceRows.current[tag] = row;
                    }}
                    type="button"
                    $selected={isOpen || isChats}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? flyoutId : undefined}
                    data-testid="orbit-page-rail-workspace"
                    onClick={() => onOpenWorkspace(tag)}
                  >
                    <IconTile $color={WORKSPACE_TILE} $size={32} $glyph={17} aria-hidden="true">
                      <Icon />
                    </IconTile>
                    <NavText>
                      <NavTitle>{title}</NavTitle>
                      <NavSubtitle>{subtitle}</NavSubtitle>
                      {isChats ? <HiddenNote>Added to this chat</HiddenNote> : null}
                    </NavText>
                    <RightOutlined aria-hidden="true" style={{ color: pg.muted, fontSize: 14 }} />
                  </WorkspaceRow>
                );
              })}
            </Section>

            {unsubscribed.status === 'ready' && unsubscribed.features.length > 0 ? (
              <Section aria-label="Unsubscribed features">
                <SectionHead>
                  <SectionTitle>Unsubscribed features</SectionTitle>
                </SectionHead>
                {unsubscribed.features.map(feature => (
                  <LockedRow
                    key={feature.id}
                    type="button"
                    onClick={() => onOpenUnsubscribed(feature.id)}
                  >
                    <IconTile $color={pg.warning} $size={31} $glyph={15} aria-hidden="true">
                      <LockOutlined />
                    </IconTile>
                    <NavText>
                      <NavTitle $muted>{feature.title}</NavTitle>
                      <NavSubtitle>{feature.subtitle}</NavSubtitle>
                    </NavText>
                  </LockedRow>
                ))}
                <RailLink type="button" onClick={() => onOpenUnsubscribed()}>
                  See All
                </RailLink>
              </Section>
            ) : null}
          </>
        )}
      </RailScroll>
      {flyoutAnchor && flyoutWorkspace !== undefined && workspaceChats !== null ? (
        <OrbitWorkspaceFlyout
          anchor={flyoutAnchor}
          label={t(`${TOrbit}.page.workspaceFlyout.label`, { name: flyoutWorkspace.title })}
          chats={workspaceChats}
          onSelectChat={onSelectChat}
          onClose={onCloseWorkspace}
        />
      ) : null}
    </Rail>
  );
};

export default OrbitPageRail;
